/**
 * 布局：每一页的大小板块怎么排 —— 顺序、半宽 / 整行、折叠、隐藏、锁定，外加侧栏和手机底栏。
 *
 * ════════════════════════════════════════════════════════
 * 为什么放在这里、为什么是纯函数
 * ════════════════════════════════════════════════════════
 * 网页要用它（按存下来的顺序排、导入布局文件时先清洗一遍），
 * API 也要用它（存之前校验）。两边各写一份的话，迟早出现
 * 「网页认为合法、服务器拒收」或者反过来 —— 那种错只会表现成「拖了没存上」。
 * 所以只有这一份，而且不依赖任何东西（没有 zod、没有 node:*），浏览器能直接引。
 *
 * ── 数据形状照原型的 layout.js ──
 *   order  容器 → 板块 id 的顺序（原型的 layoutMap）
 *   size   「组|板块」→ 'full'（整行）/ 'half'（半宽）（原型的 sizeMap）
 *   hide   「组|板块」→ true（原型的 hideMap）
 *   coll   「组|板块」→ true，只留标题栏（原型的 collMap）
 *   lock   锁定：拖不动（原型的 dragLock，默认不锁 —— 平时就能拖）
 *   tip    「每一块都能挪」那条提示出现过几次（原型的 dragTip）
 *   nav    侧栏顺序、藏掉的页面、手机底栏的 5 个（原型的 navOrder / navHide / tabIds）
 *
 * 「组|板块」而不是「容器|板块」：总览的左右两栏是同一个组，
 * 一块从左栏拖到右栏，它的折叠 / 隐藏状态要跟着走。
 */

export const LAYOUT_KIND = 'tradeguard-layout';

export type BlockSize = 'full' | 'half';

export interface NavLayout {
  /** 侧栏的页面顺序。空 = 默认顺序 */
  order: string[];
  /** 从侧栏藏起来的页面 */
  hide: Record<string, true>;
  /** 手机底栏放哪几个。空 = 默认那 5 个 */
  tabs: string[];
}

export interface LayoutDoc {
  v: 1;
  lock: boolean;
  tip: number;
  order: Record<string, string[]>;
  size: Record<string, BlockSize>;
  hide: Record<string, true>;
  coll: Record<string, true>;
  nav: NavLayout;
}

/**
 * 上限。都远大于真实用量（全站 14 页、最多的一页十几张卡片），
 * 只是为了不让一份畸形的请求把一行 jsonb 撑到几 MB。
 */
export const LAYOUT_LIMITS = {
  zones: 80,
  idsPerZone: 60,
  /** size / hide / coll 各自最多多少条 */
  keys: 600,
  idLength: 80,
  navPages: 40,
  tabs: 5,
  /** 序列化之后的字节数 */
  bytes: 48_000,
  tip: 99
} as const;

export function emptyLayout(): LayoutDoc {
  return { v: 1, lock: false, tip: 0, order: {}, size: {}, hide: {}, coll: {}, nav: { order: [], hide: {}, tabs: [] } };
}

/** 一份布局是不是「什么都没改过」。登录时判断要不要把游客在这台电脑上排的带进账号 */
export function isEmptyLayout(d: LayoutDoc): boolean {
  return !d.lock
    && Object.keys(d.order).length === 0 && Object.keys(d.size).length === 0
    && Object.keys(d.hide).length === 0 && Object.keys(d.coll).length === 0
    && d.nav.order.length === 0 && Object.keys(d.nav.hide).length === 0 && d.nav.tabs.length === 0;
}

/* ══════════════════════ 校验 ══════════════════════ */

/**
 * 容器名和板块 id：非空、不太长、没有控制字符、**没有 `|`**（那是「组|板块」的分隔符）。
 * 允许中文 —— 设置页的卡片没有编号时用的是卡片名。
 */
// eslint-disable-next-line no-control-regex
const ID_RE = /^[^\u0000-\u001f\u007f|]+$/;
export function isLayoutId(s: unknown): s is string {
  return typeof s === 'string' && s.length > 0 && s.length <= LAYOUT_LIMITS.idLength && ID_RE.test(s);
}

/** 「组|板块」 */
export function isBlockKey(s: unknown): s is string {
  if (typeof s !== 'string' || s.length > LAYOUT_LIMITS.idLength * 2 + 1) return false;
  const i = s.indexOf('|');
  return i > 0 && s.indexOf('|', i + 1) < 0 && isLayoutId(s.slice(0, i)) && isLayoutId(s.slice(i + 1));
}

export function blockKey(group: string, id: string): string {
  return group + '|' + id;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

export type LayoutParse = { ok: true; doc: LayoutDoc } | { ok: false; problem: string };

/**
 * **严格**校验。服务器存之前用：哪里不对就整份拒收，并说出是哪里。
 *
 * 为什么不「能收多少收多少」：服务器悄悄丢掉一半，网页上看到的是自己刚排好的样子，
 * 刷新之后却少了几条 —— 那是最难查的一种「没存上」。拒收的话网页会当场提示。
 */
export function parseLayout(input: unknown): LayoutParse {
  const bad = (problem: string): LayoutParse => ({ ok: false, problem });
  if (!isObj(input)) return bad('布局必须是一个对象');
  if (input['v'] !== 1) return bad('不认识这个布局版本');
  let size: number;
  try {
    size = JSON.stringify(input).length;
  } catch {
    return bad('布局序列化不了');
  }
  if (size > LAYOUT_LIMITS.bytes) return bad(`布局太大了（${size} 字节，上限 ${LAYOUT_LIMITS.bytes}）`);

  const doc = emptyLayout();

  if (typeof input['lock'] !== 'boolean') return bad('lock 必须是真或假');
  doc.lock = input['lock'];

  const tip = input['tip'];
  if (typeof tip !== 'number' || !Number.isInteger(tip) || tip < 0 || tip > LAYOUT_LIMITS.tip) {
    return bad(`tip 必须是 0 到 ${LAYOUT_LIMITS.tip} 的整数`);
  }
  doc.tip = tip;

  const order = input['order'];
  if (!isObj(order)) return bad('order 必须是一个对象');
  const zones = Object.entries(order);
  if (zones.length > LAYOUT_LIMITS.zones) return bad(`容器太多了（上限 ${LAYOUT_LIMITS.zones}）`);
  for (const [zone, ids] of zones) {
    if (!isLayoutId(zone)) return bad(`容器名不对：${(zone as string).slice(0, 40)}`);
    if (!Array.isArray(ids)) return bad(`容器 ${zone} 的顺序必须是数组`);
    if (ids.length > LAYOUT_LIMITS.idsPerZone) return bad(`容器 ${zone} 里的板块太多了`);
    const seen = new Set<string>();
    for (const id of ids) {
      if (!isLayoutId(id)) return bad(`容器 ${zone} 里有一个板块 id 不对`);
      if (seen.has(id)) return bad(`容器 ${zone} 里 ${id} 出现了两次`);
      seen.add(id);
    }
    doc.order[zone] = ids.slice() as string[];
  }

  const flags = (name: 'size' | 'hide' | 'coll'): string | null => {
    const m = input[name];
    if (!isObj(m)) return `${name} 必须是一个对象`;
    const entries = Object.entries(m);
    if (entries.length > LAYOUT_LIMITS.keys) return `${name} 的条目太多了`;
    for (const [k, v] of entries) {
      if (!isBlockKey(k)) return `${name} 里有一个键不对：${(k as string).slice(0, 40)}`;
      if (name === 'size') {
        if (v !== 'full' && v !== 'half') return `size 只能是 full 或 half`;
        doc.size[k] = v;
      } else {
        if (v !== true) return `${name} 的值只能是 true`;
        doc[name][k] = true;
      }
    }
    return null;
  };
  for (const n of ['size', 'hide', 'coll'] as const) {
    const p = flags(n);
    if (p) return bad(p);
  }

  const nav = input['nav'];
  if (!isObj(nav)) return bad('nav 必须是一个对象');
  const navList = (v: unknown, max: number, what: string): string[] | string => {
    if (!Array.isArray(v)) return `${what} 必须是数组`;
    if (v.length > max) return `${what} 太长了`;
    const seen = new Set<string>();
    for (const id of v) {
      if (!isLayoutId(id)) return `${what} 里有一个页面 id 不对`;
      if (seen.has(id)) return `${what} 里 ${id} 出现了两次`;
      seen.add(id);
    }
    return v.slice() as string[];
  };
  const no = navList(nav['order'], LAYOUT_LIMITS.navPages, 'nav.order');
  if (typeof no === 'string') return bad(no);
  const nt = navList(nav['tabs'], LAYOUT_LIMITS.tabs, 'nav.tabs');
  if (typeof nt === 'string') return bad(nt);
  const nh = nav['hide'];
  if (!isObj(nh)) return bad('nav.hide 必须是一个对象');
  if (Object.keys(nh).length > LAYOUT_LIMITS.navPages) return bad('nav.hide 太长了');
  for (const [k, v] of Object.entries(nh)) {
    if (!isLayoutId(k) || v !== true) return bad('nav.hide 里有一条不对');
    doc.nav.hide[k] = true;
  }
  doc.nav.order = no;
  doc.nav.tabs = nt;
  return { ok: true, doc };
}

/**
 * **宽松**清洗。读这台电脑上存的、或者用户导入的布局文件时用：
 * 认得的留下，不认得的扔掉，永远返回一份合法的布局。
 *
 * 这里宽松是对的：本机那份可能是旧版本留下的，导入的文件可能是手改过的 ——
 * 因为其中一条不对就整份作废，用户会觉得「我排的全没了」。
 * 清洗完的结果再交给服务器时，走的仍然是上面那道严格校验。
 */
export function sanitizeLayout(input: unknown): LayoutDoc {
  const doc = emptyLayout();
  if (!isObj(input)) return doc;
  const src = input['kind'] === LAYOUT_KIND && isObj(input['layout']) ? input['layout'] : input;
  if (!isObj(src)) return doc;

  doc.lock = src['lock'] === true;
  const tip = src['tip'];
  doc.tip = typeof tip === 'number' && Number.isInteger(tip) ? Math.min(LAYOUT_LIMITS.tip, Math.max(0, tip)) : 0;

  const order = isObj(src['order']) ? src['order'] : {};
  for (const [zone, ids] of Object.entries(order).slice(0, LAYOUT_LIMITS.zones)) {
    if (!isLayoutId(zone) || !Array.isArray(ids)) continue;
    const clean = [...new Set(ids.filter(isLayoutId))].slice(0, LAYOUT_LIMITS.idsPerZone);
    if (clean.length > 0) doc.order[zone] = clean;
  }
  const size = isObj(src['size']) ? src['size'] : {};
  for (const [k, v] of Object.entries(size).slice(0, LAYOUT_LIMITS.keys)) {
    if (isBlockKey(k) && (v === 'full' || v === 'half')) doc.size[k] = v;
  }
  for (const n of ['hide', 'coll'] as const) {
    const m = isObj(src[n]) ? src[n] : {};
    for (const [k, v] of Object.entries(m).slice(0, LAYOUT_LIMITS.keys)) {
      if (isBlockKey(k) && v === true) doc[n][k] = true;
    }
  }
  const nav = isObj(src['nav']) ? src['nav'] : {};
  const list = (v: unknown, max: number): string[] =>
    Array.isArray(v) ? [...new Set(v.filter(isLayoutId))].slice(0, max) : [];
  doc.nav.order = list(nav['order'], LAYOUT_LIMITS.navPages);
  doc.nav.tabs = list(nav['tabs'], LAYOUT_LIMITS.tabs);
  const nh = isObj(nav['hide']) ? nav['hide'] : {};
  for (const [k, v] of Object.entries(nh).slice(0, LAYOUT_LIMITS.navPages)) {
    if (isLayoutId(k) && v === true) doc.nav.hide[k] = true;
  }

  /* 最后保证它过得了严格那一关（比如整体超过字节上限）：过不了就退回空的 */
  return parseLayout(doc).ok ? doc : emptyLayout();
}

/* ══════════════════════ 应用顺序 ══════════════════════ */

/**
 * 按存下来的顺序排一个容器里的板块。
 *
 * 存过的排在前面（按存下的顺序），**新出现的接在后面**（按页面原来的顺序），
 * 存过但页面上已经没有的直接忽略。所以我们给某一页加了一张卡片、删了一张，
 * 用户排好的其他卡片都不乱，新卡片也不会丢。（照原型 layout.js 的 apply）
 */
export function applyOrder(saved: readonly string[] | undefined, ids: readonly string[]): string[] {
  if (!saved || saved.length === 0) return ids.slice();
  const present = new Set(ids);
  const out: string[] = [];
  const placed = new Set<string>();
  for (const id of saved) {
    if (present.has(id) && !placed.has(id)) {
      out.push(id);
      placed.add(id);
    }
  }
  for (const id of ids) if (!placed.has(id)) out.push(id);
  return out;
}

/**
 * 同一组的几个容器一起分配（总览的左右两栏）。
 *
 * 一块从左栏拖到右栏以后，下次打开它得出现在右栏 ——
 * 单独对每栏 applyOrder 做不到：左栏会把它当成「新出现的」又接回自己末尾。
 * 所以先按每栏存下的顺序认领，认领不到的再回它默认的那一栏。（照原型的 wrapGroup）
 */
export function distribute(
  zones: ReadonlyArray<{ zone: string; ids: readonly string[] }>,
  order: Readonly<Record<string, readonly string[]>>
): string[][] {
  const all = new Set<string>();
  for (const z of zones) for (const id of z.ids) all.add(id);
  const placed = new Set<string>();
  const out = zones.map(z => {
    const list: string[] = [];
    for (const id of order[z.zone] ?? []) {
      if (all.has(id) && !placed.has(id)) {
        list.push(id);
        placed.add(id);
      }
    }
    return list;
  });
  zones.forEach((z, i) => {
    for (const id of z.ids) {
      if (!placed.has(id)) {
        out[i]!.push(id);
        placed.add(id);
      }
    }
  });
  return out;
}

/* ══════════════════════ 侧栏与手机底栏 ══════════════════════ */

/** 这几页不许从侧栏藏：藏掉外观页就再也找不回这些设置了（照原型的 NAV_LOCK） */
export const NAV_LOCKED: readonly string[] = ['home', 'look'];

/** 侧栏按这个顺序排。新加的页面接在最后 */
export function navOrder(nav: NavLayout, pages: readonly string[]): string[] {
  return applyOrder(nav.order, pages);
}

export function isNavHidden(nav: NavLayout, page: string): boolean {
  return !NAV_LOCKED.includes(page) && nav.hide[page] === true;
}

/**
 * 手机底栏的 5 格。用户选的在前，不满 5 个用默认的补上，藏掉的页面不出现。
 * （照原型的 tabbar：底栏总是 5 格，不会出现一个侧栏里找不到的页面）
 */
export function tabIds(nav: NavLayout, defaults: readonly string[], pages: readonly string[]): string[] {
  const ok = (id: string): boolean => pages.includes(id) && !isNavHidden(nav, id);
  const mine = nav.tabs.filter(ok);
  const fill = defaults.filter(id => ok(id) && !mine.includes(id));
  const rest = pages.filter(id => ok(id) && !mine.includes(id) && !fill.includes(id));
  return [...mine, ...fill, ...rest].slice(0, LAYOUT_LIMITS.tabs);
}
